/**
 * Sign-in prompt option helpers.
 *
 * When the bot decides a user wants to (or needs to) sign in, it sends a
 * `SignInPrompt` message rendering either a single "Login" button or "Yes"/"No"
 * buttons. Selecting an affirmative option opens the sign-in modal directly on
 * the frontend, so the bot never has to round-trip a modal-trigger message back.
 */

// Option labels on a SignInPrompt that mean "yes, sign me in". Compared
// case-insensitively so backend copy changes to casing don't break the match.
const AFFIRMATIVE_SIGN_IN_OPTIONS = new Set(['login', 'yes']);

/**
 * Whether a selected option label is an affirmative sign-in choice.
 * @param {string} value The selected option label.
 * @returns {boolean}
 */
export const isAffirmativeSignInOption = value =>
  typeof value === 'string' &&
  AFFIRMATIVE_SIGN_IN_OPTIONS.has(value.trim().toLowerCase());

/**
 * Whether selecting an option should open the sign-in modal.
 *
 * True only when the option belongs to a `SignInPrompt` message and is an
 * affirmative choice ("Login"/"Yes"). A "No" selection or a freshly typed
 * message returns false so the conversation continues normally.
 * @param {{ isSignInPrompt?: boolean }} [message] The message the option belongs to.
 * @param {string} optionValue The selected option label.
 * @returns {boolean}
 */
export const shouldOpenSignInModal = (message, optionValue) =>
  Boolean(message?.isSignInPrompt) && isAffirmativeSignInOption(optionValue);
